import { z } from 'zod/v4';
import { createNamedSchema } from './helpers';

export const bytesUnitKindSchema = z.enum(['Bytes', 'BinaryBytes', 'BytesPerSec', 'BinaryBytesPerSec'] as const);
export type BytesUnitKind = z.infer<typeof bytesUnitKindSchema>;
export const bytesUnitOptionsSchema = createNamedSchema(
  'BytesUnitOptions',
  z.object({
    kind: bytesUnitKindSchema,
    decimal_places: z.number().optional(),
    abbreviate: z.boolean().optional(),
  })
);
export interface BytesUnitOptions extends z.TypeOf<typeof bytesUnitOptionsSchema> {}

export const customUnitKindSchema = z.enum(['Custom'] as const);
export type CustomUnitKind = z.infer<typeof customUnitKindSchema>;
export const customUnitOptionsSchema = createNamedSchema(
  'CustomUnitOptions',
  z.object({
    kind: customUnitKindSchema,
    value: z.string(),
    decimal_places: z.number().optional(),
    abbreviate: z.boolean().optional(),
  })
);
export interface CustomUnitOptions extends z.TypeOf<typeof customUnitOptionsSchema> {}

export const decimalUnitKindSchema = z.enum(['Decimal'] as const);
export type DecimalUnitKind = z.infer<typeof decimalUnitKindSchema>;
export const decimalUnitOptionsSchema = createNamedSchema(
  'DecimalUnitOptions',
  z.object({
    kind: decimalUnitKindSchema,
    // N.B.: Some customer schemas have null stored for decimal_places, so we need to accept it as valid.
    // We use z.preprocess to convert null to undefined before validation.
    decimal_places: z.preprocess((val) => (val === null ? undefined : val), z.number().optional()),
    abbreviate: z.boolean().optional(),
  })
);
export interface DecimalUnitOptions extends z.TypeOf<typeof decimalUnitOptionsSchema> {}

export const percentUnitKindSchema = z.enum(['Percent', 'PercentDecimal', '%'] as const);
export type PercentUnitKind = z.infer<typeof percentUnitKindSchema>;
export const percentUnitOptionsSchema = createNamedSchema(
  'PercentUnitOptions',
  z.object({
    kind: percentUnitKindSchema,
    decimal_places: z.number().optional(),
  })
);
export interface PercentUnitOptions extends z.TypeOf<typeof percentUnitOptionsSchema> {}

export const timeUnitKindSchema = z.enum([
  'Nanoseconds',
  'Microseconds',
  'Milliseconds',
  'Seconds',
  'Minutes',
  'Hours',
  'Days',
  'Weeks',
  'Months',
  'Years',
] as const);
export type TimeUnitKind = z.infer<typeof timeUnitKindSchema>;
export const timeUnitOptionsSchema = createNamedSchema(
  'TimeUnitOptions',
  z.object({
    kind: timeUnitKindSchema,
    decimal_places: z.number().optional(),
  })
);
export interface TimeUnitOptions extends z.TypeOf<typeof timeUnitOptionsSchema> {}

/**
 * UnitKind is a union of the different unit kinds.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const unitKindSchema = z.union([
  bytesUnitKindSchema,
  customUnitKindSchema,
  decimalUnitKindSchema,
  percentUnitKindSchema,
  timeUnitKindSchema,
]);
export type UnitKind = z.infer<typeof unitKindSchema>;

// UnitKindExcludeCustom is to avoid complexity with handling custom units (mainly, custom units require 'value').
// Use in situations that do not require custom units.
export type UnitKindExcludeCustom = Exclude<UnitKind, 'Custom'>;

/**
 * UnitOptions is a discriminated union of the different unit options.
 */
export const unitOptionsSchema = z.discriminatedUnion('kind', [
  bytesUnitOptionsSchema,
  customUnitOptionsSchema,
  decimalUnitOptionsSchema,
  percentUnitOptionsSchema,
  timeUnitOptionsSchema,
]);
export type UnitOptions = z.infer<typeof unitOptionsSchema>;
